import React, { useEffect, useState } from 'react';
import useUser, { IUserState } from '../stores/useUser';
import { IUserStatus } from '../utils/UserRequest'; // TODO: user IUserState ?
import styles from './LoginRegisterToggle.scss';
import { IRegisterOptions } from './RegisterForm';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import Tabs from './Tabs';
import Tab from './Tab';
import trackApi, { IWglEventProps } from '../utils/trackApi';
// import adformTrackApi from '../utils/adformTrackApi';
import { createToast } from './MaterialUi/Toast';
import HouseIcon from './svg/HouseIcon';
import SendPasswordForm from './SendPasswordForm';
import getVarsFromUrl from '../utils/getVarsFromUrl';
import SendPasswordResetForm from './SendPasswordResetForm';
import useIsMount from '../hooks/useIsMount';
// import adcellTrackApi from '../utils/adcellTrackApi';
import LogError from '../utils/LogError';
import SubscribeToNewsletterForm from './SubscribeToNewsletterForm';
import InfoBox from './RegisterEstateAgent/InfoBox';

export interface ILoginRegisterToggleProps {
    className?: string;
    defaultForm?: 'login' | 'register' | 'sendPassword' | 'resetPassword';
    // closeHandler?: () => void;
    successHandler?: (userData?: IUserStatus) => void;
    registerHandler?: (userData?: IUserStatus) => void;
    loginHandler?: (userData?: IUserStatus) => void;
    registerOptions?: IRegisterOptions; // headline/button text for register modal, used in handleGoToRegister
    loginOptions?: IRegisterOptions; // headline/button text for register modal, used in handleGoToRegister
    showMinimal?: boolean;
    showTabs?: boolean;
    trackingEvent?: IWglEventProps;
}

const urlVars = getVarsFromUrl(document.location.href);

const LoginRegisterToggle: React.FC<React.PropsWithChildren<ILoginRegisterToggleProps>> = ({
    className = '',
    successHandler = () => { },
    registerHandler = () => { },
    loginHandler = () => { },
    // closeHandler = null,
    registerOptions = {},
    loginOptions = {},
    defaultForm = 'register',
    showMinimal = false,
    showTabs = false,
    trackingEvent

}) => {

    const isMount = useIsMount();
    const currDefaultForm = urlVars?.resetPassword ? 'resetPassword' : defaultForm;
    const [activeForm, setActiveForm] = useState<ILoginRegisterToggleProps['defaultForm'] | 'subscribeNewsletter'>(currDefaultForm);
    const [name, setName] = React.useState('');
    const [pass, setPass] = React.useState('');
    const [dataProcessingAccepted, setDataProcessingAccepted] = React.useState(false);
    const [termsAccepted, setTermsAccepted] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);

    const [user, userActions] = useUser();

    useEffect(() => {
        trackApi.wglEvent({ ...trackingEvent });
    }, []);

    useEffect(() => {
        if (!isMount && trackingEvent) {
            trackingEvent.counter++;
            trackingEvent.streckenzweig1 = activeForm === 'login' ? 'Anmelden' : 'Registrieren';
            trackApi.wglEvent({ ...trackingEvent });
        }
    }, [activeForm, error]);

    useEffect(() => {
        const currDefaultForm = urlVars?.resetPassword ? 'resetPassword' : defaultForm;
        setActiveForm(currDefaultForm);
    }, [defaultForm]);

    const handleSubmit = (ev?: React.FormEvent, salt?: string, secret?: string) => {
        ev.preventDefault();
        setError(error ? '' : null);
        setIsLoading(true);

        // fix #1045 iPhone+Firefox login with password-manager: use form values instead of state-vars
        const formElms = (ev.target as HTMLFormElement).elements;
        const loginName = (formElms.namedItem('name') as HTMLInputElement).value;
        const loginPass = (formElms.namedItem('pass') as HTMLInputElement).value;
        const loginPassWithSalt = (formElms.namedItem('pass') as HTMLInputElement).value + "-" + salt + "-" + secret;

        const action =
            activeForm === 'login'
                ? userActions.login(loginName, loginPass)
                : userActions.register(loginName, loginPass, loginPassWithSalt, dataProcessingAccepted, termsAccepted);

        action
            .then((userData: IUserState) => {
                userActions.refreshStatus();
                console.debug('Login success', userData);
                if (userData) {
                    createToast('Anmeldung war erfolgreich', 'success');
                } else {
                    // admin login with redirect to two factor auth
                    return;
                }

                // close();

                if (activeForm === 'login') {
                    if (typeof loginHandler === 'function') {
                        loginHandler(userData);
                    }
                    if (typeof successHandler === 'function') {
                        successHandler(userData);
                    }
                } else {
                    const welcomeMailPromise = userActions.sendWelcomeMail('new_user');
                    const trackPromise = trackApi
                        .wglTarget('REGISTRIERUNG')
                        .catch((e) => {
                            console.error('ga error', e);
                        });

                    Promise.allSettled([welcomeMailPromise, trackPromise]).then(() => {
                        setActiveForm('subscribeNewsletter');
                    });
                }


            })
            .catch((e) => {
                console.warn('Login error', e);
                const error = e.responseData && e.responseData.message ? e.responseData.message : 'Ein Fehler ist aufgetreten.';
                setError(error);

                // status 400 means wrong user+password
                // status 403 means forbidden / flood control
                // status 422 means already registred
                if (e.statusCode !== 400 && e.statusCode !== 422 && e.statusCode !== 403) {
                    LogError.add(e, {
                        title: e?.responseData?.message || 'Login error',
                        error_code: 'LoginRegisterToggle.tsx:handleSubmit',
                        request_data: { loginName, loginPass: loginPass?.replace(/./g, '*'), dataProcessingAccepted, termsAccepted, activeForm },
                    });
                }
                setIsLoading(false);
            });
    };

    const handleRegister = () => {
        if (typeof registerHandler === 'function') {
            registerHandler(user);
        }
        if (typeof successHandler === 'function') {
            successHandler(user);
        }
    }

    const toggleToRegister = (ev: React.MouseEvent) => {
        ev.preventDefault();
        setError(null);
        setActiveForm('register');
    };
    const toggleToLogin = (ev: React.MouseEvent) => {
        ev.preventDefault();
        setError(null);
        setActiveForm('login');
    };

    const options = activeForm === 'login' ? loginOptions : registerOptions;
    const headline = activeForm === 'login'
        ? options?.headline || 'Anmeldung'
        : activeForm === 'sendPassword' || activeForm === 'resetPassword'
            ? 'Passwort-Reset'
            : activeForm === 'subscribeNewsletter'
                ? 'Für den wöchentlichen Newsletter von Wohnglück.de anmelden'
                : options?.headline || 'Neuen Account anlegen';

    const showInfoBox = !showTabs && !showMinimal && !user.isLoggedIn && (activeForm === 'register' || activeForm === 'login');

    return (
        (<div className={styles.loginRegisterToggle + ' ' + className}>
            {showTabs && (
                <Tabs activeTab={activeForm}>
                    <Tab name="register" title={'Registrieren'} href="/user/register" onClick={toggleToRegister}></Tab>
                    <Tab name="login" title={'Anmelden'} href="/user/login" onClick={toggleToLogin}></Tab>
                </Tabs>
            )}
            {!showMinimal && <h1>{headline}</h1>}

            {showInfoBox && (
                <InfoBox className={styles.infoBox}>

                    <div className={styles.toggleActions}>
                        {activeForm === 'login' ? (
                            <>
                                <span>Du hast noch keinen Account?{' '}</span>
                                <a href="/user/register" onClick={toggleToRegister}>
                                    Jetzt registrieren
                                </a>
                            </>
                        ) : (
                            <>
                                <span>Du hast bereits ein Wohnglück-Konto?</span>
                                <a href="/user/login" onClick={toggleToLogin}>
                                    Jetzt anmelden
                                </a>
                            </>
                        )}
                    </div>
                </InfoBox>
            )}

            {activeForm === 'register' ? (
                <>
                    {!!options?.description && <p>{options.description}</p>}
                    <RegisterForm
                        error={error}
                        isLoading={isLoading}
                        onSubmit={handleSubmit}
                        name={name}
                        setName={setName}
                        pass={pass}
                        setPass={setPass}
                        dataProcessingAccepted={dataProcessingAccepted}
                        setDataProcessingAccepted={setDataProcessingAccepted}
                        termsAccepted={termsAccepted}
                        setTermsAccepted={setTermsAccepted}
                        registerOptions={registerOptions}
                    />
                </>
            ) : activeForm === 'login' ? (
                <>
                    {!!options?.description && <p>{options.description}</p>}
                    <LoginForm error={error} isLoading={isLoading} handleSubmit={handleSubmit} name={name} setName={setName} pass={pass} setPass={setPass} buttonText={options.button} />
                </>
            ) : activeForm === 'sendPassword' ? (
                <>
                    <SendPasswordForm name={name} setName={setName} />

                </>
            ) : activeForm === 'resetPassword' ? (
                <>
                    <SendPasswordResetForm resetToken={decodeURIComponent(urlVars?.resetPassword)} name={decodeURIComponent(urlVars?.account?.replace(/\+/g, '%20'))} />
                </>
            ) : activeForm === 'subscribeNewsletter' ? (
                <>
                    <SubscribeToNewsletterForm onSubmit={handleRegister} onCancel={handleRegister} newsletterId="WGL_NEWSLETTER" />
                </>
            ) : null}
            {activeForm === 'login' && !showMinimal && (
                <p className={styles.forgotPassword}>
                    <a href="/user/password" onClick={(ev) => { ev.preventDefault(); setActiveForm('sendPassword'); }}>Passwort vergessen?</a>
                </p>
            )}
        </div>)
    );
};

export default LoginRegisterToggle;
